import * as THREE from "three";

/**
 * DIORAMAS : habillage 3D LOCAL, pose a quelques endroits choisis du terrain.
 *
 * POURQUOI PAS UNE COUCHE. Semer des batiments ou des cultures sur tout le
 * departement n'a pas de sens ici : a 200 m par cellule et une camera qui
 * embrasse 100 km, une maison fait un sous-pixel, et le cout de rendu serait
 * paye en permanence pour quelque chose d'illisible. Un diorama repond a un
 * autre besoin : l'animateur zoome a un endroit pendant qu'il parle de la
 * vigne ou du mais, et l'endroit s'habille. C'est une VIGNETTE, assumee comme
 * telle, pas une cartographie de l'occupation du sol.
 *
 * Consequences de ce parti pris :
 *   - rien n'est construit tant qu'on n'a pas active le diorama (la scene
 *     large ne paie rien) ;
 *   - la geometrie est a l'echelle REELLE (une maison fait 10 m), donc elle
 *     n'est lisible que de pres — c'est voulu, c'est ce qui donne l'echelle ;
 *   - les couches existantes (villes, prelevements, choropleths) ne sont pas
 *     touchees : un diorama s'ajoute par-dessus et se retire sans laisser de
 *     trace.
 *
 * ANCRAGE PAR LA DONNEE. Les emplacements ne sont pas des coordonnees ecrites
 * a la main : `pickDioramaSpots` les DERIVE du datapack (la commune qui a le
 * plus de vignes, celle qui a le plus de cereales, la ville la plus peuplee).
 * Un autre territoire donne donc d'autres emplacements sans toucher au code,
 * et la fiche affichee porte les vrais chiffres de la commune sous-jacente.
 *
 * SEMIS DETERMINISTE. Chaque diorama tire ses positions d'un PRNG a graine
 * derivee de son identifiant, jamais de Math.random() : un atelier rejoue
 * deux fois doit montrer exactement le meme village. C'est aussi la difference
 * avec le prototype foret, qui se resseme a chaque rechargement.
 */

// --- PRNG deterministe (mulberry32) ---------------------------------------

function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// --- geometries unitaires partagees par tous les archetypes ---------------

/**
 * Toit a deux pentes, base 1x1 dans le plan XZ, hauteur 1, faitage le long de
 * X, origine AU NIVEAU DE LA BASE (et non au centre) : le toit se pose donc a
 * `y = hauteur du mur` sans correction, ce qui evite une constante de calage
 * differente pour chaque batiment.
 *
 * Une pyramide (ConeGeometry a 4 pans) aurait ete plus courte a ecrire mais
 * lit comme une tour, pas comme une maison : c'est la ligne de faitage qui
 * fait le batiment d'habitation.
 */
function gableRoofGeometry() {
  const A = [-0.5, 0, -0.5];
  const B = [0.5, 0, -0.5];
  const C = [0.5, 1, 0];
  const D = [-0.5, 1, 0];
  const E = [0.5, 0, 0.5];
  const F = [-0.5, 0, 0.5];
  // Ordre des sommets choisi pour que toutes les normales pointent vers
  // l'exterieur (verifie face par face) : sans ca, computeVertexNormals
  // renvoie des faces noires sous la lumiere directionnelle.
  const tris = [
    [A, C, B], [A, D, C],   // pan avant (-Z)
    [E, C, D], [E, D, F],   // pan arriere (+Z)
    [A, F, D],              // pignon -X
    [B, C, E],              // pignon +X
  ];
  const positions = new Float32Array(tris.length * 9);
  tris.forEach((tri, i) => {
    tri.forEach((v, j) => positions.set(v, i * 9 + j * 3));
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

function makeGeometries() {
  return {
    // Rayons a 0.5 : l'echelle appliquee a l'instance est alors un DIAMETRE,
    // homogene avec la largeur des boites. Une seule convention a retenir.
    box: new THREE.BoxGeometry(1, 1, 1),
    gable: gableRoofGeometry(),
    cone: new THREE.ConeGeometry(0.5, 1, 7),
    cyl: new THREE.CylinderGeometry(0.5, 0.5, 1, 8),
    disc: new THREE.CylinderGeometry(0.5, 0.5, 1, 28),
    // Carte de vegetation : un quad vertical, texture a trous. Voir
    // buildVineyard pour ce que ca remplace.
    card: new THREE.PlaneGeometry(1, 1),
  };
}

// --- micro-relief partage --------------------------------------------------

/**
 * Bruit de valeur lisse, en METRES, fonction deterministe de (x, z).
 *
 * Le modele d'altitude a des mailles de 200 m : sous un diorama, il ne decrit
 * qu'un plan incline: parfaitement lisse, qui lit comme du carton. Ce bruit
 * lui rend un grain a l'echelle de la parcelle.
 *
 * Il est deliberement PARTAGE entre le sol et les objets poses dessus. S'il
 * n'appartenait qu'au maillage du sol, les ceps se retrouveraient enfonces
 * dans les bosses et suspendus au-dessus des creux — le defaut exact qu'on
 * venait de corriger sur l'echantillonnage. Une seule fonction, deux
 * consommateurs, aucun ecart possible.
 */
function createRelief({ seed, amplitude, cell }) {
  const rand = mulberry32(seed);
  const size = 64;
  const table = new Float32Array(size * size);
  for (let i = 0; i < table.length; i++) table[i] = (rand() * 2 - 1) * amplitude;
  const at = (i, j) =>
    table[(((j % size) + size) % size) * size + (((i % size) + size) % size)];
  const smooth = (t) => t * t * (3 - 2 * t);
  return (x, z) => {
    const fx = x / cell;
    const fz = z / cell;
    const i0 = Math.floor(fx);
    const j0 = Math.floor(fz);
    const tx = smooth(fx - i0);
    const tz = smooth(fz - j0);
    const a = at(i0, j0) + (at(i0 + 1, j0) - at(i0, j0)) * tx;
    const b = at(i0, j0 + 1) + (at(i0 + 1, j0 + 1) - at(i0, j0 + 1)) * tx;
    return a + (b - a) * tz;
  };
}

// --- textures procedurales -------------------------------------------------

/**
 * Toutes les textures sont peintes au canvas, comme `createFlowTexture` le
 * fait deja pour les rivieres : aucun fichier a servir, aucune licence a
 * tracer, et le rendu suit la charte plutot que le pack d'ou viendrait
 * l'image. Elles sont tirees d'un PRNG a graine fixe, donc identiques d'un
 * chargement a l'autre.
 */
function canvas2d(w, h) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  return [canvas, canvas.getContext("2d")];
}

function finishTexture(canvas, { repeat = true } = {}) {
  const texture = new THREE.CanvasTexture(canvas);
  if (repeat) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
  }
  texture.anisotropy = 4;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Feuillage de vigne palissee, vu de cote : une bande dense en bas, un
 * sommet dechiquete, et des trous par lesquels on voit au travers.
 *
 * Ce sont les TROUS qui font le vegetal. Une boite pleine, quelle que soit sa
 * couleur, reste une boite ; une carte a bord irregulier et a jour lit comme
 * une haie des la premiere image, y compris de loin ou seule la silhouette
 * subsiste.
 */
function makeVineFoliageTexture() {
  const [canvas, ctx] = canvas2d(512, 128);
  const rand = mulberry32(0x5eed17);
  // Profil du sommet, lisse puis bruite : sans lui la carte garde un bord
  // superieur rectiligne qui trahit le quad.
  const profile = [];
  for (let x = 0; x <= 512; x += 8) {
    profile.push(30 + Math.sin(x * 0.035) * 10 + (rand() - 0.5) * 16);
  }
  const topAt = (x) => {
    const i = Math.min(profile.length - 2, Math.floor(x / 8));
    const t = x / 8 - i;
    return profile[i] + (profile[i + 1] - profile[i]) * t;
  };

  for (let k = 0; k < 2600; k++) {
    const x = rand() * 512;
    const top = topAt(x);
    // Densite croissante vers le bas : la haie est pleine a hauteur de fil
    // et s'effiloche vers la cime.
    const y = top + Math.pow(rand(), 0.65) * (128 - top);
    const r = 5 + rand() * 11;
    const shade = rand();
    const g = Math.round(96 + shade * 62);
    const rr = Math.round(38 + shade * 44);
    const bb = Math.round(30 + shade * 30);
    ctx.fillStyle = `rgba(${rr},${g},${bb},${0.72 + rand() * 0.28})`;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * (0.6 + rand() * 0.5), rand() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }

  // Quelques trouees franches : une haie reelle laisse passer le ciel.
  ctx.globalCompositeOperation = "destination-out";
  for (let k = 0; k < 45; k++) {
    const x = rand() * 512;
    const y = topAt(x) + rand() * (128 - topAt(x)) * 0.8;
    ctx.beginPath();
    ctx.ellipse(x, y, 3 + rand() * 7, 3 + rand() * 6, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";
  return finishTexture(canvas);
}

/** Sol calcaire travaille entre les rangs, avec la trace des passages. */
function makeSoilTexture() {
  const [canvas, ctx] = canvas2d(256, 256);
  const rand = mulberry32(0x50113);
  ctx.fillStyle = "#b6a482";
  ctx.fillRect(0, 0, 256, 256);
  for (let k = 0; k < 5000; k++) {
    const shade = rand();
    const v = Math.round(150 + shade * 60);
    ctx.fillStyle = `rgba(${v},${Math.round(v * 0.9)},${Math.round(v * 0.72)},${0.15 + rand() * 0.3})`;
    ctx.fillRect(rand() * 256, rand() * 256, 1 + rand() * 3, 1 + rand() * 3);
  }
  for (let k = 0; k < 260; k++) {
    ctx.strokeStyle = `rgba(122,104,78,${0.05 + rand() * 0.13})`;
    ctx.lineWidth = 0.6 + rand();
    const y = rand() * 256;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(85, y + (rand() - 0.5) * 9, 170, y + (rand() - 0.5) * 9, 256, y);
    ctx.stroke();
  }
  return finishTexture(canvas);
}

/** Moellons calcaires du cabanon. */
function makeStoneTexture() {
  const [canvas, ctx] = canvas2d(128, 128);
  const rand = mulberry32(0x5701e);
  ctx.fillStyle = "#8e8168";
  ctx.fillRect(0, 0, 128, 128);
  const rowHeight = 15;
  for (let y = 0; y < 128; y += rowHeight) {
    let x = -rand() * 20;
    while (x < 128) {
      const w = 14 + rand() * 20;
      const shade = rand();
      const v = Math.round(178 + shade * 44);
      ctx.fillStyle = `rgb(${v},${Math.round(v * 0.95)},${Math.round(v * 0.82)})`;
      ctx.fillRect(x + 1.2, y + 1.2, w - 2.4, rowHeight - 2.4);
      x += w;
    }
  }
  return finishTexture(canvas);
}

/** Tuiles canal du cabanon, vues de dessus. */
function makeTileTexture() {
  const [canvas, ctx] = canvas2d(128, 128);
  const rand = mulberry32(0x7011e);
  ctx.fillStyle = "#a4522f";
  ctx.fillRect(0, 0, 128, 128);
  for (let x = 0; x < 128; x += 9) {
    const shade = rand();
    ctx.fillStyle = `rgba(${Math.round(196 + shade * 46)},${Math.round(108 + shade * 44)},${Math.round(72 + shade * 32)},0.9)`;
    ctx.fillRect(x, 0, 7, 128);
    ctx.fillStyle = "rgba(90,40,24,0.35)";
    ctx.fillRect(x + 7, 0, 2, 128);
  }
  return finishTexture(canvas);
}

// --- atelier de construction ----------------------------------------------

/**
 * Un « bac » regroupe toutes les pieces qui partagent une geometrie et un
 * materiau : elles finiront dans un seul InstancedMesh, donc un seul draw
 * call. Un village de 200 maisons + son eglise + ses arbres tient ainsi en
 * cinq draw calls.
 *
 * Chaque piece retient son altitude BRUTE (`rawH`, non exageree) : c'est ce
 * qui permet de repositionner tout le diorama quand le curseur d'exageration
 * bouge, sans le reconstruire — meme principe que la foret et les
 * prelevements.
 */
function createWorkshop({ sampleLocalHeight, geometries, relief }) {
  const buckets = [];
  const textures = [];

  const bucket = (geometryKey, material, options = {}) => {
    const b = {
      geometry: geometries[geometryKey],
      material,
      items: [],
      castShadow: options.castShadow === true,
      receiveShadow: options.receiveShadow === true,
    };
    buckets.push(b);
    return b;
  };

  /** Enregistre une texture pour qu'elle soit liberee avec le diorama. */
  const registerTexture = (texture) => {
    textures.push(texture);
    return texture;
  };

  /** Pose une piece au sol local (x, z). Retourne false hors terrain. */
  const place = (b, item) => {
    const rawH = sampleLocalHeight(item.x, item.z);
    if (rawH == null) return false;
    b.items.push({
      x: item.x,
      z: item.z,
      rawH,
      // Micro-relief : ajoute APRES exageration, comme pour le sol, pour
      // qu'il reste une hauteur physique et non un decalage multiplie.
      relief: relief(item.x, item.z),
      y: item.y ?? 0,
      sx: item.sx ?? 1,
      sy: item.sy ?? 1,
      sz: item.sz ?? 1,
      rotY: item.rotY ?? 0,
      color: item.color || null,
    });
    return true;
  };

  /**
   * Rang de culture drape : une longue boite unique flotterait au-dessus des
   * creux et s'enfoncerait dans les bosses des qu'il y a de la pente (et il y
   * en a : on est en Ardeche, avec une exageration verticale x2,5). Le rang
   * est donc decoupe en troncons courts, chacun cale sur SA hauteur de sol.
   * Le leger chevauchement (1.06) masque les marches entre troncons.
   */
  const placeRow = (b, { cx, cz, angle, length, width, height, segment, color, y }) => {
    const count = Math.max(1, Math.round(length / segment));
    const step = length / count;
    const dx = Math.cos(angle);
    const dz = Math.sin(angle);
    for (let i = 0; i < count; i++) {
      const t = -length / 2 + step * (i + 0.5);
      place(b, {
        x: cx + dx * t,
        z: cz + dz * t,
        y: y ?? height / 2,
        sx: step * 1.06,
        sy: height,
        sz: width,
        // Une rotation Y de theta envoie l'axe local +X sur (cos theta, 0,
        // -sin theta) : pour orienter la piece selon (cos angle, 0, sin
        // angle), il faut donc theta = -angle. Meme convention partout.
        rotY: -angle,
        color,
      });
    }
  };

  /**
   * Parcourt les troncons d'un rang sans rien poser : le decoupage (donc le
   * drapage sur le relief) est le meme que placeRow, mais l'appelant decide
   * ce qu'il met sur chaque troncon. C'est ce qui permet d'empiler plusieurs
   * pieces par troncon — feuillage, souche, fil, piquet — la ou placeRow n'en
   * pose qu'une.
   */
  const eachRowSegment = ({ cx, cz, angle, length, segment }, visit) => {
    const count = Math.max(1, Math.round(length / segment));
    const step = length / count;
    const dx = Math.cos(angle);
    const dz = Math.sin(angle);
    for (let i = 0; i < count; i++) {
      const t = -length / 2 + step * (i + 0.5);
      visit({ x: cx + dx * t, z: cz + dz * t, length: step, index: i });
    }
  };

  return { bucket, place, placeRow, eachRowSegment, registerTexture, buckets, textures };
}

/**
 * Socle de sol local : une nappe fine, subdivisee et texturee, posee sur le
 * terrain dans le rayon du diorama.
 *
 * Le maillage du terrain a des cellules de 200 m. Sous un diorama de 300 m de
 * rayon, cela fait trois facettes — une plaque lisse, sans grain ni matiere,
 * sur laquelle les objets ont l'air deposes plutot qu'implantes. Ce socle est
 * ce qui manque le plus au realisme, avant toute amelioration de la geometrie
 * des objets eux-memes.
 *
 * Il s'efface en fondu sur son pourtour (alpha par sommet) : une bordure nette
 * dessinerait un disque de moquette pose sur la carte.
 */
function buildGroundPatch({ sampleLocalHeight, relief, radius, spec }) {
  const segments = spec.segments ?? 108;
  const step = (radius * 2) / segments;
  const side = segments + 1;
  const gridIndex = new Int32Array(side * side).fill(-1);
  const vx = [];
  const vz = [];
  const vRawH = [];
  const vRelief = [];
  const vAlpha = [];
  let count = 0;

  for (let j = 0; j < side; j++) {
    for (let i = 0; i < side; i++) {
      const x = -radius + i * step;
      const z = -radius + j * step;
      const distance = Math.hypot(x, z);
      if (distance > radius) continue;
      const rawH = sampleLocalHeight(x, z);
      if (rawH == null) continue;
      gridIndex[j * side + i] = count++;
      vx.push(x);
      vz.push(z);
      vRawH.push(rawH);
      vRelief.push(relief(x, z));
      vAlpha.push(Math.min(1, (radius - distance) / (radius * 0.3)));
    }
  }
  if (count < 3) return null;

  const indices = [];
  for (let j = 0; j < segments; j++) {
    for (let i = 0; i < segments; i++) {
      const a = gridIndex[j * side + i];
      const b = gridIndex[j * side + i + 1];
      const c = gridIndex[(j + 1) * side + i];
      const d = gridIndex[(j + 1) * side + i + 1];
      if (a < 0 || b < 0 || c < 0 || d < 0) continue;
      indices.push(a, c, b, b, c, d);
    }
  }
  if (!indices.length) return null;

  const positions = new Float32Array(count * 3);
  const uvs = new Float32Array(count * 2);
  // Couleur a QUATRE composantes : three passe alors vColor en vec4 et
  // applique l'alpha, ce qui donne le fondu de bord sans texture dediee.
  const colors = new Float32Array(count * 4);
  const base = new THREE.Color(spec.color);
  const uvScale = spec.uvScale ?? 11;
  for (let i = 0; i < count; i++) {
    positions[i * 3] = vx[i];
    positions[i * 3 + 2] = vz[i];
    uvs[i * 2] = vx[i] / uvScale;
    uvs[i * 2 + 1] = vz[i] / uvScale;
    colors[i * 4] = base.r;
    colors[i * 4 + 1] = base.g;
    colors[i * 4 + 2] = base.b;
    colors[i * 4 + 3] = vAlpha[i];
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 4));
  geometry.setIndex(indices);

  const material = new THREE.MeshStandardMaterial({
    map: spec.map,
    vertexColors: true,
    transparent: true,
    roughness: 0.97,
    // Le socle et le terrain sont a quelques centimetres l'un de l'autre :
    // sans decalage de profondeur ils clignotent l'un a travers l'autre.
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;

  const lift = spec.lift ?? 0.12;
  const applyExaggeration = (ex) => {
    const attribute = geometry.attributes.position;
    for (let i = 0; i < count; i++) {
      attribute.setY(i, vRawH[i] * ex + vRelief[i] + lift);
    }
    attribute.needsUpdate = true;
    geometry.computeVertexNormals();
  };

  return { mesh, geometry, material, applyExaggeration };
}

/**
 * Materiau d'un archetype. Pas d'ombres portees sur les dioramas : la shadow
 * map de la scene couvre 100 km avec 2048 texels (≈ 50 m par texel), elle ne
 * peut RIEN dire d'utile sur un objet de 10 m — elle ne produirait que de
 * l'acne d'ombre sur les toits. L'eclairage directionnel suffit a detacher
 * les faces.
 */
function propMaterial(color, roughness = 0.85) {
  return new THREE.MeshStandardMaterial({ color, roughness, flatShading: true });
}

function lerpColor(a, b, t) {
  return a.clone().lerp(b, t);
}

/**
 * Parcellaire : quelques parcelles disposees en couronne autour de l'ancrage,
 * chacune avec SA propre orientation.
 *
 * L'orientation est tiree sans remise dans une liste d'angles distincts, et
 * non au hasard. C'est le detail qui fait tout : deux parcelles voisines qui
 * partagent leur sens de plantation fusionnent visuellement en une seule
 * masse, et le parcellaire — ce qui devait etre lu — disparait. Le premier
 * essai tirait les angles librement et donnait un unique bloc dore.
 */
function layoutParcels({ rand, radius, count, spread, length, depth }) {
  const angles = [0, Math.PI / 6, Math.PI / 3, Math.PI / 2, (2 * Math.PI) / 3, (5 * Math.PI) / 6];
  const offset = Math.floor(rand() * angles.length);
  const parcels = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + (rand() - 0.5) * 0.7;
    const r = radius * (spread[0] + (spread[1] - spread[0]) * rand());
    parcels.push({
      cx: Math.cos(a) * r,
      cz: Math.sin(a) * r,
      angle: angles[(offset + i) % angles.length],
      length: length[0] + rand() * length[1],
      depth: depth[0] + rand() * depth[1],
    });
  }
  return parcels;
}

// --- archetype : bourg -----------------------------------------------------

const STONE_A = new THREE.Color("#d8cdb6");
const STONE_B = new THREE.Color("#a89980");
const TILE_A = new THREE.Color("#b8552f");
const TILE_B = new THREE.Color("#d99162");

/**
 * Bourg : maisons alignees sur une trame de rues, densite decroissante vers
 * l'exterieur, eglise au centre.
 *
 * L'alignement fait tout le travail de lecture. Un semis aleatoire de boites
 * lit comme un tas de gravats ; les memes boites posees sur une trame lisent
 * comme un village, meme sans dessiner une seule rue. On se contente donc
 * d'orienter et d'espacer, et de laisser des trouees regulieres la ou
 * passeraient les rues transversales.
 */
function buildTown({ workshop, rand, radius }) {
  const { bucket, place } = workshop;
  const walls = bucket("box", propMaterial("#ffffff"));
  const roofs = bucket("gable", propMaterial("#ffffff"));
  const spires = bucket("cone", propMaterial("#5b6b6e"));
  const trunks = bucket("cyl", propMaterial("#6b4a34", 0.95));
  const canopies = bucket("cone", propMaterial("#4f8440"));

  const streetAngle = rand() * Math.PI;
  const dx = Math.cos(streetAngle);
  const dz = Math.sin(streetAngle);
  // Repere du bourg : u le long des rues, v en travers.
  const toLocal = (u, v) => [u * dx - v * dz, u * dz + v * dx];

  const alongStep = 15;   // maison + mitoyennete
  const acrossStep = 32;  // rue + jardins arriere
  const half = Math.ceil(radius / alongStep);
  const halfV = Math.ceil(radius / acrossStep);

  for (let iv = -halfV; iv <= halfV; iv++) {
    for (let iu = -half; iu <= half; iu++) {
      // Rues transversales : une trouee tous les sept modules.
      if (((iu % 7) + 7) % 7 === 3) continue;
      const u = iu * alongStep + (rand() - 0.5) * 3;
      const v = iv * acrossStep + (rand() - 0.5) * 5;
      const distance = Math.sqrt(u * u + v * v);
      const d = distance / radius;
      if (d > 1) continue;
      // Place devant l'eglise : sans ce vide, les maisons se batissent
      // par-dessus le clocher et on perd le seul repere d'echelle du bourg.
      if (distance < 34) continue;
      // Densite : coeur plein, faubourgs clairsemes. Sans ca le bourg a des
      // bords nets et lit comme une maquette decoupee.
      if (rand() > 1 - Math.pow(d, 1.7) * 0.95) continue;

      const [x, z] = toLocal(u, v);
      const w = 8 + rand() * 5;
      const depth = 8 + rand() * 4;
      const wallH = 5 + rand() * 5;
      const roofH = depth * (0.35 + rand() * 0.15);
      const wallColor = lerpColor(STONE_A, STONE_B, rand());
      const roofColor = lerpColor(TILE_A, TILE_B, rand());
      const rotY = -streetAngle + (rand() - 0.5) * 0.12;

      const placed = place(walls, {
        x, z, y: wallH / 2, sx: w, sy: wallH, sz: depth, rotY, color: wallColor,
      });
      if (!placed) continue;
      place(roofs, {
        x, z, y: wallH, sx: w * 1.12, sy: roofH, sz: depth * 1.12, rotY, color: roofColor,
      });
    }
  }

  // Eglise : meme jeu de pieces, a une autre echelle. C'est le repere qui
  // dit « bourg » plutot que « lotissement », et il donne l'echelle des
  // maisons autour.
  const naveColor = lerpColor(STONE_A, STONE_B, 0.65);
  const rotY = -streetAngle;
  place(walls, { x: 0, z: 0, y: 6, sx: 30, sy: 12, sz: 15, rotY, color: naveColor });
  place(roofs, { x: 0, z: 0, y: 12, sx: 31, sy: 7, sz: 16.5, rotY, color: lerpColor(TILE_A, TILE_B, 0.2) });
  const [tx, tz] = toLocal(-17, 0);
  place(walls, { x: tx, z: tz, y: 13, sx: 9, sy: 26, sz: 9, rotY, color: naveColor });
  place(spires, { x: tx, z: tz, y: 26 + 6, sx: 12, sy: 12, sz: 12, rotY });

  // Arbres : quelques-uns dans les trouees, pour casser l'uniformite des
  // toits et rappeler la foret alentour.
  for (let i = 0; i < 40; i++) {
    const a = rand() * Math.PI * 2;
    const r = radius * (0.35 + 0.7 * rand());
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const h = 7 + rand() * 5;
    if (!place(trunks, { x, z, y: h * 0.25, sx: 0.8, sy: h * 0.5, sz: 0.8 })) continue;
    place(canopies, {
      x, z, y: h * 0.5 + h * 0.35, sx: 5 + rand() * 3, sy: h * 0.7, sz: 5 + rand() * 3,
      color: lerpColor(new THREE.Color("#3f7a3a"), new THREE.Color("#6a9c47"), rand()),
    });
  }
}

// --- archetype : vignoble --------------------------------------------------

const VINE_A = new THREE.Color("#4a6b3c");
const VINE_B = new THREE.Color("#6d8a4a");

/**
 * Vignoble : parcelles orientees, rangs palisses, sol travaille.
 *
 * ARCHETYPE DE REFERENCE POUR LE RENDU. Les deux autres dioramas sont restes
 * en volumes pleins ; celui-ci porte le traitement complet, ce qui permet de
 * comparer les deux dans la meme scene avant de generaliser.
 *
 * Ce qui a change, et pourquoi :
 *
 * 1. LE RANG N'EST PLUS UNE BOITE. Un rang de vigne, c'est du feuillage
 *    troue : deux cartes verticales texturees en alpha-test, legerement
 *    ecartees pour donner de l'epaisseur, plus la souche et le fil de
 *    palissage. Une carte a bord dechiquete coute moins qu'une boite et lit
 *    comme du vegetal des la premiere image — c'est le trou qui fait la
 *    plante, pas le polygone.
 * 2. LE SOL EXISTE. Un socle subdivise et texture remplace la facette de
 *    200 m du modele d'altitude (cf. buildGroundPatch).
 * 3. LES OMBRES SONT VRAIES. Les pieces projettent et recoivent, la lumiere
 *    de la scene etant recadree sur le diorama pendant le gros plan (cf.
 *    enterCloseUp cote page).
 *
 * Ce qui ne change pas : c'est toujours le MOTIF — lignes regulieres, terre
 * nue entre les rangs, orientation propre a chaque parcelle — qui fait lire
 * « vigne » plutot que « champ ». Le detail ne remplace pas la composition,
 * il la recompense quand on s'approche.
 */
function buildVineyard({ workshop, rand, radius }) {
  const { bucket, place, eachRowSegment, registerTexture } = workshop;

  const foliageTexture = registerTexture(makeVineFoliageTexture());
  const stoneTexture = registerTexture(makeStoneTexture());
  const tileTexture = registerTexture(makeTileTexture());

  const foliage = bucket(
    "card",
    new THREE.MeshStandardMaterial({
      map: foliageTexture,
      // alphaTest plutot que transparent : pas de tri par profondeur a faire,
      // et l'ombre portee reprend la decoupe du feuillage au lieu d'un
      // rectangle plein.
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      roughness: 0.85,
    }),
    { castShadow: true, receiveShadow: true }
  );
  const stocks = bucket("box", propMaterial("#5d4a35", 0.95), { castShadow: true });
  const posts = bucket("cyl", propMaterial("#9a8b70", 0.95), { castShadow: true });
  const wires = bucket("box", propMaterial("#b8b4a8", 0.5));
  const walls = bucket(
    "box",
    new THREE.MeshStandardMaterial({ map: stoneTexture, roughness: 0.95 }),
    { castShadow: true, receiveShadow: true }
  );
  const roofs = bucket(
    "gable",
    new THREE.MeshStandardMaterial({ map: tileTexture, roughness: 0.8 }),
    { castShadow: true }
  );
  const cypress = bucket("cone", propMaterial("#2f5233", 0.9), { castShadow: true });

  const parcels = layoutParcels({
    rand, radius, count: 5, spread: [0.18, 0.62], length: [95, 55], depth: [70, 45],
  });

  const rowGap = 2.6;   // ecartement reel d'un vignoble palisse
  const canopyBase = 0.62;
  const canopyHeight = 1.15;
  const canopyMid = canopyBase + canopyHeight / 2;

  parcels.forEach((p) => {
    const nx = -Math.sin(p.angle);
    const nz = Math.cos(p.angle);
    // Perpendiculaire au rang, en repere monde : sert a ecarter les deux
    // cartes de feuillage de part et d'autre de l'axe du rang.
    const rotY = -p.angle;
    const rowCount = Math.floor(p.depth / rowGap);

    for (let i = 0; i < rowCount; i++) {
      const off = -p.depth / 2 + rowGap * (i + 0.5);
      const cx = p.cx + nx * off;
      const cz = p.cz + nz * off;
      const tint = lerpColor(VINE_A, VINE_B, 0.35 + rand() * 0.5);

      eachRowSegment(
        { cx, cz, angle: p.angle, length: p.length, segment: 7 },
        ({ x, z, length, index }) => {
          // Miroir un troncon sur deux : la meme texture repetee a
          // l'identique le long du rang se voit comme un motif, alors qu'un
          // retournement suffit a casser la repetition.
          const flip = index % 2 === 0 ? 1 : -1;
          [-0.26, 0.26].forEach((side) => {
            place(foliage, {
              x: x + nx * side,
              z: z + nz * side,
              y: canopyMid,
              sx: length * 1.04 * flip,
              sy: canopyHeight,
              rotY,
              color: tint,
            });
          });
          // Souche : la partie ligneuse sous le feuillage. Sans elle le rang
          // flotte, parce que le feuillage s'arrete a 60 cm du sol.
          place(stocks, {
            x, z, y: canopyBase / 2, sx: length, sy: canopyBase, sz: 0.22,
            rotY, color: tint.clone().multiplyScalar(0.5),
          });
          // Fil de palissage, au-dessus du feuillage.
          place(wires, {
            x, z, y: canopyBase + canopyHeight + 0.1,
            sx: length, sy: 0.035, sz: 0.035, rotY,
          });
          if (index % 2 === 0) {
            place(posts, { x, z, y: 0.95, sx: 0.1, sy: 1.9, sz: 0.1 });
          }
        }
      );

      // Piquets de tete de rang, plus gros : ce sont eux qui dessinent la
      // limite de parcelle.
      if (i % 2 === 0) {
        [-1, 1].forEach((s) => {
          place(posts, {
            x: cx + Math.cos(p.angle) * (p.length / 2) * s,
            z: cz + Math.sin(p.angle) * (p.length / 2) * s,
            y: 1.05, sx: 0.18, sy: 2.1, sz: 0.18,
          });
        });
      }
    }
  });

  // Cabanon de vigne + cypres : le repere d'echelle du vignoble.
  const ca = rand() * Math.PI * 2;
  const cr = radius * 0.32;
  const hx = Math.cos(ca) * cr;
  const hz = Math.sin(ca) * cr;
  if (place(walls, { x: hx, z: hz, y: 2.4, sx: 7.5, sy: 4.8, sz: 5.5, rotY: ca })) {
    place(roofs, { x: hx, z: hz, y: 4.8, sx: 8.2, sy: 2.3, sz: 6.1, rotY: ca });
    // Cheminee : le petit accident qui empeche le volume de se lire comme
    // une primitive.
    place(walls, {
      x: hx + Math.cos(ca) * 2.4, z: hz + Math.sin(ca) * 2.4,
      y: 6.4, sx: 0.8, sy: 3.2, sz: 0.8, rotY: ca,
    });
  }
  for (let i = 0; i < 4; i++) {
    const a = ca + 0.5 + i * 0.35;
    const cxp = hx + Math.cos(a) * (12 + i * 5);
    const czp = hz + Math.sin(a) * (12 + i * 5);
    const h = 10 + rand() * 4;
    // Deux cones empiles : la silhouette effilee du cypres, qu'un cone seul
    // ne donne pas.
    if (!place(cypress, { x: cxp, z: czp, y: h * 0.32, sx: 2.6, sy: h * 0.64, sz: 2.6 })) continue;
    place(cypress, { x: cxp, z: czp, y: h * 0.76, sx: 1.9, sy: h * 0.5, sz: 1.9 });
  }
}

// --- archetype : cereales irriguees ---------------------------------------

const MAIZE_A = new THREE.Color("#7d9c3f");
const MAIZE_B = new THREE.Color("#a9bb51");

/**
 * Champ de cereales irriguees : rangs hauts et serres, donc une masse pleine
 * (l'inverse exact du vignoble, ou la terre nue se voit entre les rangs), et
 * le materiel d'irrigation qui va avec.
 *
 * Le materiel n'est pas de la decoration : c'est le propos de l'atelier. Une
 * parcelle de mais sans enrouleur ni retenue ne raconte rien sur l'eau ; avec
 * eux, on voit d'ou vient l'eau et ce qu'elle dessert. On a prefere
 * l'enrouleur et la retenue collinaire au pivot central : c'est le materiel
 * qu'on trouve reellement sur ces parcellaires, et un pivot rigide de 150 m
 * traverserait le relief au lieu de le suivre.
 */
function buildIrrigatedCrop({ workshop, rand, radius }) {
  const { bucket, place, placeRow } = workshop;
  const rows = bucket("box", propMaterial("#ffffff", 0.9));
  const metal = bucket("box", propMaterial("#c8543f", 0.6));
  const drum = bucket("cyl", propMaterial("#dfe4e6", 0.5));
  const wheels = bucket("cyl", propMaterial("#2b2b2b", 0.9));
  const hose = bucket("box", propMaterial("#1f5f6b", 0.6));
  const basinRim = bucket("disc", propMaterial("#8a7d63", 0.95));
  const basinWater = bucket("disc", new THREE.MeshStandardMaterial({
    color: "#4fb3d4", roughness: 0.2,
  }));

  const parcels = layoutParcels({
    rand, radius, count: 4, spread: [0.22, 0.6], length: [100, 50], depth: [72, 40],
  });

  const rowGap = 1.35; // rangs serres : la parcelle doit lire comme un bloc
  parcels.forEach((p) => {
    const nx = -Math.sin(p.angle);
    const nz = Math.cos(p.angle);
    const count = Math.floor(p.depth / rowGap);
    for (let i = 0; i < count; i++) {
      const off = -p.depth / 2 + rowGap * (i + 0.5);
      placeRow(rows, {
        cx: p.cx + nx * off,
        cz: p.cz + nz * off,
        angle: p.angle,
        length: p.length,
        width: 1.1,
        height: 2.5,
        segment: 10,
        color: lerpColor(MAIZE_A, MAIZE_B, rand() * 0.9),
      });
    }
  });

  // Retenue collinaire : la reserve qui alimente les enrouleurs. Placee a
  // l'ecart des parcelles, sur le point le plus bas teste — une retenue se
  // remplit par gravite, la poser sur une bosse serait faux.
  let basin = null;
  for (let i = 0; i < 12; i++) {
    const a = rand() * Math.PI * 2;
    const r = radius * (0.55 + 0.2 * rand());
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const h = workshop.sampleLocalHeight ? workshop.sampleLocalHeight(x, z) : null;
    if (h == null) continue;
    if (!basin || h < basin.h) basin = { x, z, h };
  }
  if (basin) {
    place(basinRim, { x: basin.x, z: basin.z, y: 0.9, sx: 52, sy: 2.4, sz: 38 });
    place(basinWater, { x: basin.x, z: basin.z, y: 1.7, sx: 45, sy: 1.2, sz: 32 });
  }

  // Enrouleurs en bout de parcelle, avec leur tuyau deroule vers le champ.
  parcels.slice(0, 2).forEach((p) => {
    const nx = -Math.sin(p.angle);
    const nz = Math.cos(p.angle);
    const ex = p.cx + nx * (p.depth / 2 + 9);
    const ez = p.cz + nz * (p.depth / 2 + 9);
    const rotY = -p.angle;
    if (!place(metal, { x: ex, z: ez, y: 1.5, sx: 4.5, sy: 1.8, sz: 2.6, rotY })) return;
    // Tambour : un cylindre debout aplati suffit a lire « bobine » a cette
    // taille, sans avoir a le coucher sur son axe.
    place(drum, { x: ex, z: ez, y: 2.7, sx: 3.2, sy: 2.4, sz: 3.2, rotY });
    [-1.6, 1.6].forEach((o) => {
      place(wheels, {
        x: ex + Math.cos(p.angle) * o, z: ez + Math.sin(p.angle) * o,
        y: 0.6, sx: 1.2, sy: 0.5, sz: 1.2, rotY,
      });
    });
    // Tuyau deroule : segmente comme les rangs, donc drape sur le relief.
    placeRow(hose, {
      cx: (ex + p.cx) / 2, cz: (ez + p.cz) / 2,
      angle: Math.atan2(p.cz - ez, p.cx - ex),
      length: Math.hypot(p.cx - ex, p.cz - ez),
      width: 0.5, height: 0.4, segment: 8, y: 0.2,
    });
  });
}

// --- registre des archetypes ----------------------------------------------

/**
 * `frame` est le rapport distance camera / rayon du diorama. Il ne se deduit
 * pas du rayon : un bourg REMPLIT son disque, alors qu'un parcellaire n'en
 * occupe qu'une fraction. A rapport egal, le village cadre juste et les
 * parcelles paraissent lointaines — chaque archetype porte donc le sien.
 */
export const DIORAMA_KINDS = {
  town: {
    label: "Bourg",
    accent: "#db6c52",
    radius: 330,
    frame: 2.6,
    build: buildTown,
    caption:
      "Bâti à l'échelle réelle (maisons de 8 à 13 m), posé sur la trame de rues. " +
      "Illustration : l'implantation n'est pas relevée sur le cadastre.",
  },
  vineyard: {
    label: "Vignoble",
    accent: "#9c5f9e",
    radius: 300,
    // Cadrage plus serre que les autres : le feuillage, les fils et le grain
    // du sol ne se voient qu'a cette distance. Un rendu detaille qu'on ne
    // cadre pas assez pres ne sert a rien.
    frame: 1.55,
    build: buildVineyard,
    // Socle de sol local et ombres portees : seul cet archetype les porte
    // pour l'instant, c'est le prototype de rendu.
    ground: { color: "#bcaa87", uvScale: 11, texture: makeSoilTexture },
    relief: { amplitude: 0.32, cell: 16 },
    shadows: true,
    caption:
      "Rangs palissés espacés de 2,6 m, orientation par parcelle. " +
      "Les surfaces sont celles de la commune, leur emplacement est illustratif.",
  },
  crop: {
    label: "Céréales irriguées",
    accent: "#c9a227",
    radius: 330,
    frame: 2.1,
    build: buildIrrigatedCrop,
    caption:
      "Rangs serrés de 2,5 m de haut, enrouleurs et retenue collinaire. " +
      "Le poste agricole du recensement est « Céréales », maïs compris.",
  },
};

// --- choix des emplacements, derives du datapack --------------------------

function cultureSurface(commune, libelle) {
  const postes = commune?.["agriculture.cultures.postes"] || [];
  const poste = postes.find((p) => p.libelle === libelle);
  return poste?.surface_ha ?? null;
}

function bestCommuneFor(communes, libelle) {
  let best = null;
  (communes || []).forEach((commune) => {
    const surface = cultureSurface(commune, libelle);
    if (surface == null) return;
    if (!best || surface > best.surface) best = { commune, surface };
  });
  return best;
}

function fact(label, value) {
  return value == null || value === "" ? null : { label, value };
}

/**
 * Emplacements des dioramas pour un territoire.
 *
 * Rien n'est code en dur : on cherche dans le datapack la commune qui porte
 * le plus de vignes, celle qui porte le plus de cereales, et la ville la plus
 * peuplee. Un autre departement produit donc d'autres emplacements sans
 * toucher a ce fichier — et la fiche du diorama affiche les chiffres reels de
 * la commune sur laquelle il est pose, ce qui evite l'ecueil du decor qui ne
 * dit rien.
 */
export function pickDioramaSpots({ communes, cities }) {
  const spots = [];

  const city = (cities || []).slice().sort((a, b) => (b.population || 0) - (a.population || 0))[0];
  if (city) {
    const record = (communes || []).find((c) => c.code_insee === city.code_insee);
    spots.push({
      id: "town",
      kind: "town",
      title: city.name,
      subtitle: "Bourg — bâti illustratif",
      x: city.x,
      y: city.y,
      cityCodeInsee: city.code_insee,
      // La pastille est decalee de ~1,4 km au sud-est du marqueur de ville :
      // sans ca elle serait posee sous le faisceau de la ville, qui est bien
      // plus haut et capterait tous les clics. Le diorama, lui, reste ancre
      // sur la ville.
      pinOffset: { dx: 1400, dy: -1400 },
      facts: [
        fact("Population", `${(city.population || 0).toLocaleString("fr-FR")} hab.`),
        fact("Densité", record && `${record["population.densite_hab_km2"]} hab./km²`),
        fact("Restrictions sécheresse", record && `${record["secheresse.synthese.jours_restriction_par_an"]} j/an`),
      ].filter(Boolean),
    });
  }

  const vine = bestCommuneFor(communes, "Vignes");
  if (vine) {
    const c = vine.commune;
    spots.push({
      id: "vineyard",
      kind: "vineyard",
      title: c.nom_commune,
      subtitle: "Premier vignoble du territoire",
      x: c.x,
      y: c.y,
      facts: [
        fact("Vignes", `${vine.surface.toLocaleString("fr-FR")} ha`),
        fact("Part de la SAU", `${cultureShare(c, "Vignes")} %`),
        fact("SAU irriguée", shareIrrigated(c)),
      ].filter(Boolean),
    });
  }

  const cereals = bestCommuneFor(communes, "Céréales");
  if (cereals) {
    const c = cereals.commune;
    spots.push({
      id: "crop",
      kind: "crop",
      title: c.nom_commune,
      subtitle: "Premières céréales du territoire",
      x: c.x,
      y: c.y,
      facts: [
        fact("Céréales", `${cereals.surface.toLocaleString("fr-FR")} ha`),
        fact("Part de la SAU", `${cultureShare(c, "Céréales")} %`),
        fact("SAU irriguée", shareIrrigated(c)),
        fact("Restrictions sécheresse", `${c["secheresse.synthese.jours_restriction_par_an"]} j/an`),
      ].filter(Boolean),
    });
  }

  return spots;
}

function cultureShare(commune, libelle) {
  const postes = commune?.["agriculture.cultures.postes"] || [];
  return postes.find((p) => p.libelle === libelle)?.part_sau_pct ?? null;
}

function shareIrrigated(commune) {
  const pct = commune?.["agriculture.irrigation.part_sau_irriguee_pct"];
  if (pct == null) return commune?.["agriculture.irrigation.commune_irrigante"] ? "commune irrigante" : null;
  const ha = commune["agriculture.irrigation.surface_irriguee_ha"];
  return ha == null ? `${pct} %` : `${pct} % (${ha.toLocaleString("fr-FR")} ha)`;
}

// --- construction / cycle de vie ------------------------------------------

/**
 * Construit le diorama d'un emplacement. Appele PARESSEUSEMENT, au premier
 * affichage : tant qu'un diorama n'a pas ete demande, il ne coute ni memoire
 * ni temps de chargement.
 *
 * Retourne de quoi le piloter ensuite sans le reconstruire :
 *   - `group`      a ajouter a la scene et dont on bascule `visible` ;
 *   - `anchor`     le point du terrain vise (pour cadrer la camera) ;
 *   - `applyExaggeration(ex)` a rappeler quand le curseur vertical bouge ;
 *   - `dispose()`  pour liberer geometries et materiaux.
 */
export function buildDiorama(spot, { sampleAt, sampleLocalHeight, exaggeration }) {
  const kind = DIORAMA_KINDS[spot.kind];
  if (!kind) return null;
  const anchorSample = sampleAt(spot.x, spot.y);
  if (!anchorSample) return null;

  const geometries = makeGeometries();
  // Semis local a l'emplacement : les positions sont exprimees autour de
  // (0,0) puis translatees. C'est ce qui rend le meme archetype reutilisable
  // a n'importe quel endroit du terrain.
  const originX = anchorSample.x;
  const originZ = anchorSample.z;
  const localSample = (x, z) => sampleLocalHeight(originX + x, originZ + z);

  // Micro-relief propre a l'emplacement, partage par le socle de sol et par
  // tout ce qui se pose dessus. Un archetype qui ne declare pas de relief
  // recoit la fonction nulle : les objets restent exactement sur la surface
  // du terrain, comme avant.
  const relief = kind.relief
    ? createRelief({
        seed: seedFromString(`${spot.id}-relief`),
        amplitude: kind.relief.amplitude,
        cell: kind.relief.cell,
      })
    : () => 0;

  const workshop = createWorkshop({ sampleLocalHeight: localSample, geometries, relief });
  workshop.sampleLocalHeight = localSample;
  const rand = mulberry32(seedFromString(spot.id));
  kind.build({ workshop, rand, radius: kind.radius });

  const group = new THREE.Group();
  group.position.set(originX, 0, originZ);
  group.visible = false;

  // Socle de sol : ajoute EN PREMIER pour qu'il soit dessine avant ce qui le
  // recouvre, sa transparence de bord se composant alors correctement.
  const ground = kind.ground
    ? buildGroundPatch({
        sampleLocalHeight: localSample,
        relief,
        radius: kind.radius,
        spec: { ...kind.ground, map: workshop.registerTexture(kind.ground.texture()) },
      })
    : null;
  if (ground) group.add(ground.mesh);

  const parts = [];
  const dummy = new THREE.Object3D();
  workshop.buckets.forEach((b) => {
    if (!b.items.length) {
      b.material.dispose();
      return;
    }
    const mesh = new THREE.InstancedMesh(b.geometry, b.material, b.items.length);
    mesh.frustumCulled = false; // le groupe entier tient dans quelques centaines de metres
    mesh.castShadow = b.castShadow;
    mesh.receiveShadow = b.receiveShadow;
    b.items.forEach((item, i) => {
      if (item.color) mesh.setColorAt(i, item.color);
    });
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    group.add(mesh);
    parts.push({ mesh, items: b.items });
  });

  const applyExaggeration = (ex) => {
    parts.forEach(({ mesh, items }) => {
      items.forEach((item, i) => {
        dummy.position.set(item.x, item.rawH * ex + item.relief + item.y, item.z);
        dummy.rotation.set(0, item.rotY, 0);
        dummy.scale.set(item.sx, item.sy, item.sz);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
    });
    if (ground) ground.applyExaggeration(ex);
  };
  applyExaggeration(exaggeration);

  const instanceCount = parts.reduce((n, p) => n + p.items.length, 0);

  return {
    spot,
    kind,
    group,
    anchor: { x: originX, z: originZ, rawH: anchorSample.rawH },
    radius: kind.radius,
    frameDistance: kind.radius * (kind.frame ?? 2.4),
    // Lu par la page pour recadrer la lumiere de la scene sur le diorama.
    shadows: kind.shadows === true,
    instanceCount,
    applyExaggeration,
    dispose() {
      parts.forEach(({ mesh }) => {
        mesh.material.dispose();
        mesh.dispose();
      });
      if (ground) {
        ground.geometry.dispose();
        ground.material.dispose();
      }
      workshop.textures.forEach((t) => t.dispose());
      Object.values(geometries).forEach((g) => g.dispose());
      group.clear();
    },
  };
}

// --- pastilles d'appel ----------------------------------------------------

/**
 * Pastille posee au sol a l'emplacement d'un diorama : c'est elle qu'on clique
 * pour habiller la zone. Volontairement discrete et differente des marqueurs
 * de ville (anneau + faisceau + etiquette), qu'on ne touche pas : un demi-ton
 * plus bas, sans faisceau, pour qu'elle se lise comme une invitation et non
 * comme une donnee de plus sur la carte.
 */
export function createDioramaPin(spot, { sampleAt, worldDiag, exaggeration }) {
  const kind = DIORAMA_KINDS[spot.kind];
  const offset = spot.pinOffset;
  const sample =
    (offset && sampleAt(spot.x + offset.dx, spot.y + offset.dy)) || sampleAt(spot.x, spot.y);
  if (!kind || !sample) return null;

  const group = new THREE.Group();
  group.position.set(sample.x, sample.rawH * exaggeration, sample.z);

  // Assez grande pour se voir et se cliquer depuis la vue departementale,
  // plus petite que l'anneau d'une ville de premier rang : la pastille
  // invite, elle ne concurrence pas la donnee.
  const r = Math.max(worldDiag * 0.0058, 340);
  const padGeo = new THREE.CircleGeometry(r, 6);
  padGeo.rotateX(-Math.PI / 2);
  const pad = new THREE.Mesh(
    padGeo,
    new THREE.MeshBasicMaterial({
      color: kind.accent, transparent: true, opacity: 0.28,
      side: THREE.DoubleSide, depthWrite: false,
    })
  );
  pad.position.y = 8;
  group.add(pad);

  const edgeGeo = new THREE.RingGeometry(r * 0.94, r, 6);
  edgeGeo.rotateX(-Math.PI / 2);
  const edge = new THREE.Mesh(
    edgeGeo,
    new THREE.MeshBasicMaterial({
      color: kind.accent, transparent: true, opacity: 0.95,
      side: THREE.DoubleSide, depthWrite: false,
    })
  );
  edge.position.y = 9;
  group.add(edge);

  const hitGeo = new THREE.CylinderGeometry(r * 1.15, r * 1.15, 260, 8);
  const hitMesh = new THREE.Mesh(hitGeo, new THREE.MeshBasicMaterial({ visible: false }));
  hitMesh.position.y = 130;
  group.add(hitMesh);

  return { group, pad, edge, hitMesh, rawH: sample.rawH, spot };
}

export function disposeDioramaPin(pin) {
  [pin.pad, pin.edge, pin.hitMesh].forEach((m) => {
    m.geometry.dispose();
    m.material.dispose();
  });
}
